/**
 * Calendar model shared by the calendar composites (DESIGN.md T13): local-time date helpers, the overlap layout of
 * a day column and the per-calendar colour classes. Pure functions, no i18n (the apps pass formatted labels).
 */

import type { ReactNode } from 'react';

export const CALENDAR_COLORS = ['blue', 'green', 'purple', 'orange', 'teal', 'pink', 'yellow', 'red'] as const;
export type CalendarColor = (typeof CALENDAR_COLORS)[number];

/** Event chips: left accent bar in the calendar colour, light tint, normal text colour (contrast in both themes). */
export const CALENDAR_COLOR_CLASSES: Readonly<Record<CalendarColor, { chip: string; swatch: string; tentative: string }>> = {
  blue: { chip: 'border-cal-blue bg-cal-blue/15', swatch: 'bg-cal-blue border-cal-blue', tentative: 'border-cal-blue bg-cal-blue/5' },
  green: { chip: 'border-cal-green bg-cal-green/15', swatch: 'bg-cal-green border-cal-green', tentative: 'border-cal-green bg-cal-green/5' },
  purple: { chip: 'border-cal-purple bg-cal-purple/15', swatch: 'bg-cal-purple border-cal-purple', tentative: 'border-cal-purple bg-cal-purple/5' },
  orange: { chip: 'border-cal-orange bg-cal-orange/15', swatch: 'bg-cal-orange border-cal-orange', tentative: 'border-cal-orange bg-cal-orange/5' },
  teal: { chip: 'border-cal-teal bg-cal-teal/15', swatch: 'bg-cal-teal border-cal-teal', tentative: 'border-cal-teal bg-cal-teal/5' },
  pink: { chip: 'border-cal-pink bg-cal-pink/15', swatch: 'bg-cal-pink border-cal-pink', tentative: 'border-cal-pink bg-cal-pink/5' },
  yellow: { chip: 'border-cal-yellow bg-cal-yellow/15', swatch: 'bg-cal-yellow border-cal-yellow', tentative: 'border-cal-yellow bg-cal-yellow/5' },
  red: { chip: 'border-cal-red bg-cal-red/15', swatch: 'bg-cal-red border-cal-red', tentative: 'border-cal-red bg-cal-red/5' },
};

/** An event as the grids draw it (the app maps its API model to this). */
export interface CalendarGridEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  color: CalendarColor;
  /** Second line (location). */
  detail?: string | undefined;
  /** Tentative / not answered: lighter, dashed. */
  tentative?: boolean | undefined;
  /** Busy block or private event of someone else: no details, muted. */
  muted?: boolean | undefined;
  cancelled?: boolean | undefined;
  /** Invitation not answered yet: bold (needs action). */
  pending?: boolean | undefined;
  /** Declined (shown only when the user keeps declined events visible): struck through, faded. */
  declined?: boolean | undefined;
  /** Show-as marker of the left bar (Outlook): free = outlined, tentative = hatched, away = purple, elsewhere = dotted. */
  showAs?: 'free' | 'tentative' | 'busy' | 'oof' | 'workingElsewhere' | undefined;
  /** Category colours: small dots after the title. */
  categories?: readonly { name: string; color: CalendarColor }[] | undefined;
  /** Small icons/markers after the title (recurring, meeting …), rendered by the app. */
  badges?: ReactNode;
}

export const DAY_MS = 86_400_000;

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Calendar days later (DST-safe: local midnight to local midnight). */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, date.getHours(), date.getMinutes());
}

export function addMonths(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), last));
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** First day of the week containing `date` (weekStartsOn: 0 = Sunday, 1 = Monday). */
export function startOfWeek(date: Date, weekStartsOn: number): Date {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() - weekStartsOn + 7) % 7));
}

export interface WeekRules { weekStartsOn: number; minimalDays: number }
export const ISO_WEEK_RULES: Readonly<WeekRules> = Object.freeze({ weekStartsOn: 1, minimalDays: 4 });

/** Calendar-field arithmetic, independent of timezone offsets and 23/25-hour DST days. */
export function calendarWeek(date: Date, rules: WeekRules = ISO_WEEK_RULES): { week: number; year: number } {
  if (!Number.isInteger(rules.weekStartsOn) || rules.weekStartsOn < 0 || rules.weekStartsOn > 6 || !Number.isInteger(rules.minimalDays) || rules.minimalDays < 1 || rules.minimalDays > 7) throw new RangeError('Invalid calendar week rules');
  const utcDay = (year: number, month: number, day: number) => { const value = new Date(0); value.setUTCFullYear(year, month, day); value.setUTCHours(0, 0, 0, 0); return value.getTime(); };
  const ordinal = utcDay(date.getFullYear(), date.getMonth(), date.getDate());
  const firstWeek = (year: number) => {
    const anchor = utcDay(year, 0, rules.minimalDays);
    return anchor - ((new Date(anchor).getUTCDay() - rules.weekStartsOn + 7) % 7) * DAY_MS;
  };
  let year = date.getFullYear();
  if (ordinal < firstWeek(year)) year--;
  else if (ordinal >= firstWeek(year + 1)) year++;
  return { week: Math.floor((ordinal - firstWeek(year)) / (7 * DAY_MS)) + 1, year };
}

/** Existing ISO API delegates to the shared regional week arithmetic. */
export function isoWeek(date: Date): number { return calendarWeek(date).week; }

/** The 6 × 7 days shown for the month of `date`. */
export function monthMatrix(date: Date, weekStartsOn: number): Date[][] {
  const first = startOfWeek(new Date(date.getFullYear(), date.getMonth(), 1), weekStartsOn);
  return Array.from({ length: 6 }, (_, week) => Array.from({ length: 7 }, (_, day) => addDays(first, week * 7 + day)));
}

/** Does the event touch the day [dayStart, dayStart + 1 day)? */
export function overlapsDay(event: Pick<CalendarGridEvent, 'start' | 'end'>, day: Date): boolean {
  const start = startOfDay(day);
  const end = addDays(start, 1);
  return event.start < end && (event.end > start || (event.end.getTime() === event.start.getTime() && event.start >= start));
}

export interface PositionedEvent {
  event: CalendarGridEvent;
  /** Fractions of the day column (0..1). */
  top: number;
  height: number;
  /** Overlap column and the number of columns of its overlap group. */
  column: number;
  columns: number;
}

/**
 * Timed events of one day placed in columns like Outlook: events that overlap share the width; each overlap group
 * (transitively overlapping events) uses as many columns as it needs. Minimum height: 15 minutes.
 */
export function layoutDay(events: readonly CalendarGridEvent[], day: Date): PositionedEvent[] {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = addDays(startOfDay(day), 1).getTime();
  const span = dayEnd - dayStart;
  const timed = events
    .filter((e) => !e.allDay && e.end.getTime() - e.start.getTime() < DAY_MS && e.start.getTime() < dayEnd && e.end.getTime() > dayStart)
    .map((event) => {
      const start = Math.max(event.start.getTime(), dayStart);
      const end = Math.max(Math.min(event.end.getTime(), dayEnd), start + 15 * 60_000);
      return { event, start, end };
    })
    .sort((a, b) => a.start - b.start || b.end - a.end);

  const out: PositionedEvent[] = [];
  let group: { item: (typeof timed)[number]; column: number }[] = [];
  let groupEnd = -Infinity;
  const flush = () => {
    const columns = Math.max(1, ...group.map((g) => g.column + 1));
    for (const { item, column } of group) {
      out.push({ event: item.event, top: (item.start - dayStart) / span, height: (item.end - item.start) / span, column, columns });
    }
    group = [];
  };
  for (const item of timed) {
    if (item.start >= groupEnd && group.length > 0) {
      flush();
      groupEnd = -Infinity;
    }
    // First column whose last event has ended.
    const used = new Map<number, number>();
    for (const g of group) used.set(g.column, Math.max(used.get(g.column) ?? 0, g.item.end));
    let column = 0;
    while ((used.get(column) ?? -Infinity) > item.start) column++;
    group.push({ item, column });
    groupEnd = Math.max(groupEnd, item.end);
  }
  if (group.length > 0) flush();
  return out;
}

/** Events of the all-day row for a day (all-day events and timed events spanning more than one day). */
export function allDayEvents(events: readonly CalendarGridEvent[], day: Date): CalendarGridEvent[] {
  return events.filter((e) => (e.allDay || e.end.getTime() - e.start.getTime() >= DAY_MS) && overlapsDay(e, day));
}

/** An event's piece within one week row of the month grid. */
export interface WeekSegment {
  event: CalendarGridEvent;
  /** Columns (0-6) of the first and last day in this week. */
  from: number;
  to: number;
  lane: number;
  /** The event continues before / after this week. */
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/** Spans multiple days (all-day over several days or timed over 24 h): drawn as a bar. */
export const isMultiDay = (event: Pick<CalendarGridEvent, 'start' | 'end' | 'allDay'>): boolean =>
  event.allDay ? event.end.getTime() - event.start.getTime() > DAY_MS + 3_600_000 : event.end.getTime() - event.start.getTime() >= DAY_MS;

/**
 * Month week layout (Outlook/Google): multi-day and all-day events first as bars spanning their days, then the
 * single-day events; each gets the lowest free lane. With `maxLanes`, a day with more events than lanes keeps the
 * last lane for "+n more": `shown` are the visible segments, `hidden[col]` the events of a day that did not fit.
 */
export function layoutWeek(events: readonly CalendarGridEvent[], week: readonly Date[], maxLanes: number): { shown: WeekSegment[]; hidden: CalendarGridEvent[][] } {
  const first = week[0];
  if (!first) return { shown: [], hidden: [] };
  const columnOf = (date: Date) => Math.round((startOfDay(date).getTime() - startOfDay(first).getTime()) / DAY_MS);
  const segments = events
    .filter((event) => week.some((day) => overlapsDay(event, day)))
    .map((event) => {
      const lastDay = event.end.getTime() > event.start.getTime() && event.end.getTime() === startOfDay(event.end).getTime() ? addDays(event.end, -1) : event.end;
      const start = columnOf(event.start);
      const end = columnOf(lastDay);
      return { event, from: Math.max(0, start), to: Math.min(week.length - 1, Math.max(start, end)), continuesBefore: start < 0, continuesAfter: end > week.length - 1, lane: 0 };
    })
    .sort((a, b) => Number(isMultiDay(b.event) || b.event.allDay) - Number(isMultiDay(a.event) || a.event.allDay) || a.from - b.from || b.to - b.from - (a.to - a.from) || a.event.start.getTime() - b.event.start.getTime());
  const used: boolean[][] = [];
  for (const segment of segments) {
    let lane = 0;
    while (Array.from({ length: segment.to - segment.from + 1 }, (_, i) => used[lane]?.[segment.from + i]).some(Boolean)) lane++;
    segment.lane = lane;
    used[lane] ??= [];
    for (let col = segment.from; col <= segment.to; col++) used[lane]![col] = true;
  }
  const count = week.map((_, col) => segments.filter((s) => s.from <= col && s.to >= col).length);
  const overflow = (col: number) => (count[col] ?? 0) > maxLanes;
  const visible = (s: WeekSegment) => s.lane < maxLanes - 1 || (s.lane === maxLanes - 1 && Array.from({ length: s.to - s.from + 1 }, (_, i) => s.from + i).every((col) => !overflow(col)));
  const shown = segments.filter(visible);
  const hidden = week.map((_, col) => segments.filter((s) => !visible(s) && s.from <= col && s.to >= col).map((s) => s.event));
  return { shown, hidden };
}
